import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ProposalsIcon } from '../../assets';
import { PERMISSIONS } from '../../config/constants';
import { replaceIds, ROUTES } from '../../config/routes/config';
import { CREATE_PROPOSAL } from '../../graphql/proposal/mutation';
import { usePermissions } from '../../hooks/usePermissions';
import type { ResCreateProposal } from '../../types/proposal';
import { Button } from '../common/Button';
import { BrandImage } from '../images/BrandImage';
import type { ClientBrandItem } from '../inputs/ClientSearchPanel';
import { ClientSearchPanel } from '../inputs/ClientSearchPanel';
import { DatePickerInput } from '../inputs/DatePicker';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { TextInput } from '../inputs/TextInput';
import { UserDropdownList } from '../inputs/UserDropdownList';
import { PopupLayout } from './PopupLayout';

type TCreateProposalForm = {
  name: string;
  campaign_type: 'one_time' | 'recurring';
  month?: string;
  influencerIds: Array<{ value: string; id: string }>;
};

export const CreateProposal = ({ refetch }: { refetch: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const { checkPermission } = usePermissions();

  const [selectedBrand, setSelectedBrand] = useState<ClientBrandItem | null>(
    null,
  );

  const { ...methods } = useForm<TCreateProposalForm>({
    defaultValues: { campaign_type: 'one_time' },
  });
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const [createProposal, { loading }] =
    useMutation<ResCreateProposal>(CREATE_PROPOSAL);

  const onSubmit = (data: TCreateProposalForm) => {
    if (!selectedBrand) return;
    const influencerIds = (data.influencerIds || []).map((i) => i.value);
    createProposal({
      variables: {
        name: data.name,
        brand_id: selectedBrand.id,
        month:
          data.campaign_type === 'one_time' && data.month
            ? dayjs(data.month).format('MM/YYYY')
            : dayjs().format('MM/YYYY'),
        campaign_type: data.campaign_type,
        influencer_ids: influencerIds.length > 0 ? influencerIds : undefined,
      },
      onCompleted: (res) => {
        navigate(
          replaceIds({
            url: ROUTES.SINGLE_PROPOSAL,
            params: [{ proposalId: res.createProposal.data?.id || '' }],
          }),
        );
        reset();
        setOpen(false);
        setSelectedBrand(null);
        refetch();
      },
      onError: () => {},
    });
  };

  const closeModal = () => {
    reset();
    setOpen(false);
    setSelectedBrand(null);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style="light-blue"
        text={t('proposals.createProposal')}
        id="createProposal"
        iconLeft={<ProposalsIcon />}
        customClassnames={`${!checkPermission(PERMISSIONS.CREATE_PROPOSAL) && 'hidden'}`}
      />
      {open && (
        <PopupLayout
          title={t('proposals.createProposalTitle')}
          closeModal={closeModal}
          icon={<ProposalsIcon />}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-row gap-x-6 mt-5 items-stretch"
            >
              {/* LEFT — Client/Prospect search panel (includes prospects) */}
              <ClientSearchPanel
                includeProspects
                selectedBrand={selectedBrand}
                onSelect={setSelectedBrand}
                control={control}
                categoryFieldName="__clientCategory"
              />

              {/* RIGHT — Proposal details */}
              <div className="flex flex-col gap-y-4 w-1/2">
                {/* Selected client read-only */}
                <div className="flex flex-col gap-y-1">
                  <p className="text-white font-semibold text-sm">
                    {t('proposals.createClientPlaceholder')}
                  </p>
                  <div className="flex flex-row items-center gap-x-3 bg-white rounded-[4px] px-3 py-2 min-h-[41px]">
                    {selectedBrand ? (
                      <>
                        <BrandImage
                          image={selectedBrand.profile_picture}
                          size="size-[24px]"
                        />
                        <span className="text-dark-blue text-sm font-medium">
                          {selectedBrand.name}
                        </span>
                        {selectedBrand.is_prospect && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-influentia-light-blue text-dark-blue ml-auto shrink-0">
                            {t('global.prospect')}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-[#5A5A5A] text-sm">
                        {t('proposals.createClientPlaceholder')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Proposal name */}
                <TextInput
                  register={register}
                  registerName="name"
                  type="text"
                  label={t('proposals.createName')}
                  placeholder={t('proposals.createNamePlaceholder')}
                  validation={{ required: t('proposals.nameRequired') }}
                  errors={errors.name}
                />

                {/* Campaign type */}
                <GenericDropdown
                  control={control}
                  name="campaign_type"
                  label="Tipo de campaña"
                  isClearable={false}
                  options={[
                    { value: 'one_time', label: 'One Time' },
                    { value: 'recurring', label: 'Recurrente' },
                  ]}
                />

                {/* Month — only for one_time */}
                {watch('campaign_type') === 'one_time' && (
                  <DatePickerInput
                    name="month"
                    monthYearPicker
                    isClearable
                    label={t('singleCampaign.campaignMonth')}
                    control={control}
                    fullSpace
                  />
                )}

                {/* Influencers */}
                <UserDropdownList
                  type="influencer"
                  accessName="influencerIds"
                  label={t('proposals.createInfluencers')}
                  accessKey="value"
                  addText={t('proposals.createInfluencersAdd')}
                  placeholderSelects={t(
                    'proposals.createInfluencersPlaceholder',
                  )}
                  control={control}
                  error={
                    errors.influencerIds?.root
                      ? t('proposals.addInfluencerError')
                      : ''
                  }
                  menuPosition="fixed"
                />

                {/* Save */}
                <div className="flex justify-end mt-auto">
                  <div className="w-[150px]">
                    <Button
                      style="light-blue"
                      id="saveProposal"
                      text={t('global.save')}
                      customClassnames="w-full justify-center"
                      type="submit"
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </PopupLayout>
      )}
    </>
  );
};
